import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Breadcrumb,
    Button,
    Card,
    Col,
    Row,
    Spinner,
    Table,
} from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { Button as PrimeButton } from 'primereact/button';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import '../../assets/css/performance-review.css';

const STATUS_VARIANT = {
    draft: 'secondary',
    in_progress: 'info',
    completed: 'success',
    acknowledged: 'primary',
};

const STATUS_LABEL = {
    draft: 'Draft',
    in_progress: 'In progress',
    completed: 'Submitted',
    acknowledged: 'Acknowledged',
};

const isReviewPeriodOpen = (review) => {
    if (review?.period_open === false) return false;
    if (review?.period_open === true) return true;
    if (!review?.review_period_start) return true;
    return moment().isSameOrAfter(moment(review.review_period_start), 'day');
};

const classificationClass = (label) => {
    if (!label) return '';
    if (label.includes('Leader')) return 'is-leader';
    if (label.includes('High')) return 'is-high';
    if (label.includes('Solid')) return 'is-solid';
    if (label.includes('Action')) return 'is-action';
    return 'is-critical';
};

const MyPerformanceReviews = () => {
    const { accessToken } = useAuth();
    const navigate = useNavigate();
    const [reviews, setReviews] = useState([]);
    const [teamReviews, setTeamReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showPendingPopup, setShowPendingPopup] = useState(false);
    const [popupDismissed, setPopupDismissed] = useState(false);

    const load = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        setError('');
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const [mineRes, teamRes] = await Promise.all([
                axios.get(`${config.apiBASEURL}/performance-review/my`, { headers }),
                axios.get(`${config.apiBASEURL}/performance-review/team-remarks`, { headers }).catch(() => ({ data: [] })),
            ]);
            const list = mineRes.data || [];
            const team = teamRes.data || [];
            setReviews(list);
            setTeamReviews(team);
            const pending = list.filter(
                (r) =>
                    (r.status === 'draft' || r.status === 'in_progress') && isReviewPeriodOpen(r)
            );
            const pendingTeamRemarks = team.filter((r) => !r.has_manager_remark && !r.manager_remark);
            if ((pending.length > 0 || pendingTeamRemarks.length > 0) && !popupDismissed) {
                setShowPendingPopup(true);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Could not load your performance reviews');
        } finally {
            setLoading(false);
        }
    }, [accessToken, popupDismissed]);

    useEffect(() => { load(); }, [load]);

    const pendingReviews = reviews.filter(
        (r) => (r.status === 'draft' || r.status === 'in_progress') && isReviewPeriodOpen(r)
    );
    const scheduledReviews = reviews.filter(
        (r) => (r.status === 'draft' || r.status === 'in_progress') && !isReviewPeriodOpen(r)
    );
    const pendingFill = pendingReviews.length;
    const firstPending = pendingReviews[0];
    const pendingRemarks = teamReviews.filter((r) => !r.has_manager_remark && !r.manager_remark);
    const firstPendingRemark = pendingRemarks[0];

    const closePendingPopup = () => {
        setShowPendingPopup(false);
        setPopupDismissed(true);
    };

    return (
        <div className="pr-list-page">
            <div className="body_content">
                <Row className="g-3">
                    <Col xs={12}>
                        <Breadcrumb className="mb-0">
                            <Breadcrumb.Item active>My Performance Reviews</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>

                    <Col xs={12}>
                        <div className="pr-list-header">
                            <div>
                                <h1 className="pr-page-title mb-1">My Performance Reviews</h1>
                                <p className="pr-page-subtitle mb-0">
                                    Complete assigned self-reviews and track acknowledgement from admin.
                                </p>
                            </div>
                        </div>
                    </Col>

                    {scheduledReviews.length > 0 && (
                        <Col xs={12}>
                            <Alert variant="secondary" className="mb-0 py-2">
                                <strong>{scheduledReviews.length}</strong> review(s) are scheduled for a future
                                period — you can fill them once the start date is reached.
                            </Alert>
                        </Col>
                    )}

                    {pendingFill > 0 && (
                        <Col xs={12}>
                            <Alert variant="info" className="mb-0 py-2">
                                You have <strong>{pendingFill}</strong> self-review(s) to complete.
                            </Alert>
                        </Col>
                    )}

                    {pendingRemarks.length > 0 && (
                        <Col xs={12}>
                            <Alert variant="warning" className="mb-0 py-2">
                                You have <strong>{pendingRemarks.length}</strong> report self-review(s) awaiting your manager remark.
                            </Alert>
                        </Col>
                    )}

                    <Col xs={12}>
                        <Card className="pr-list-card">
                            <Card.Header className="pr-list-card-header">
                                <span className="pr-result-count">{reviews.length} review(s)</span>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {loading && (
                                    <div className="pr-empty-state">
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Loading…
                                    </div>
                                )}

                                {!loading && error && (
                                    <Alert variant="danger" className="m-3">{error}</Alert>
                                )}

                                {!loading && !error && reviews.length === 0 && (
                                    <div className="pr-empty-state">
                                        <div className="pr-empty-icon">📋</div>
                                        <h6 className="mb-2">No reviews yet</h6>
                                        <p className="text-muted small mb-0">
                                            When admin assigns a template to you, it will appear here.
                                        </p>
                                    </div>
                                )}

                                {!loading && !error && reviews.length > 0 && (
                                    <div className="table-responsive">
                                        <Table hover className="pr-table mb-0 align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Template</th>
                                                    <th>Period</th>
                                                    <th>Score</th>
                                                    <th>Classification</th>
                                                    <th>Status</th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reviews.map((review) => {
                                                    const periodOpen = isReviewPeriodOpen(review);
                                                    const canFill =
                                                        ['draft', 'in_progress'].includes(review.status)
                                                        && periodOpen;
                                                    const isScheduled =
                                                        ['draft', 'in_progress'].includes(review.status)
                                                        && !periodOpen;
                                                    return (
                                                        <tr key={review.review_id}>
                                                            <td className="small">{review.template_name || '—'}</td>
                                                            <td className="small text-nowrap">
                                                                {moment(review.review_period_start).format('DD MMM YYYY')}
                                                                <span className="text-muted mx-1">–</span>
                                                                {moment(review.review_period_end).format('DD MMM YYYY')}
                                                            </td>
                                                            <td>
                                                                <span className="pr-score-cell">
                                                                    {review.final_score ?? '—'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {review.classification ? (
                                                                    <span className={`pr-classification-badge ${classificationClass(review.classification)}`}>
                                                                        {review.classification}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">—</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <Badge bg={STATUS_VARIANT[review.status] || 'secondary'}>
                                                                    {isScheduled
                                                                        ? `Opens ${moment(review.review_period_start).format('DD MMM YYYY')}`
                                                                        : STATUS_LABEL[review.status] || review.status}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-end">
                                                                <Button
                                                                    size="sm"
                                                                    variant={canFill ? 'primary' : 'outline-primary'}
                                                                    onClick={() =>
                                                                        navigate(`/Graphe/dashboard/my_performance_review/${review.review_id}`)
                                                                    }
                                                                >
                                                                    {canFill
                                                                        ? 'Fill review'
                                                                        : isScheduled
                                                                            ? 'View (scheduled)'
                                                                            : 'View'}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {!loading && teamReviews.length > 0 && (
                        <Col xs={12}>
                            <Card className="pr-list-card">
                                <Card.Header className="pr-list-card-header">
                                    <div>
                                        <strong>Team reviews — manager remarks</strong>
                                        <div className="text-muted small">
                                            Self-reviews from people who report to you (org hierarchy).
                                        </div>
                                    </div>
                                    <span className="pr-result-count">{teamReviews.length} review(s)</span>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="pr-table mb-0 align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Employee</th>
                                                    <th>Template</th>
                                                    <th>Period</th>
                                                    <th>Score</th>
                                                    <th>Remark</th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {teamReviews.map((review) => {
                                                    const needsRemark = !review.has_manager_remark && !review.manager_remark;
                                                    return (
                                                        <tr key={`team-${review.review_id}`}>
                                                            <td>
                                                                <strong>{review.employee?.name || '—'}</strong>
                                                            </td>
                                                            <td className="small">{review.template_name || '—'}</td>
                                                            <td className="small text-nowrap">
                                                                {moment(review.review_period_start).format('DD MMM YYYY')}
                                                                <span className="text-muted mx-1">–</span>
                                                                {moment(review.review_period_end).format('DD MMM YYYY')}
                                                            </td>
                                                            <td>
                                                                <span className="pr-score-cell">
                                                                    {review.final_score ?? '—'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {needsRemark ? (
                                                                    <Badge bg="warning" text="dark">Pending</Badge>
                                                                ) : (
                                                                    <Badge bg="success">Submitted</Badge>
                                                                )}
                                                            </td>
                                                            <td className="text-end">
                                                                <Button
                                                                    size="sm"
                                                                    variant={needsRemark ? 'primary' : 'outline-primary'}
                                                                    onClick={() =>
                                                                        navigate(`/Graphe/dashboard/my_performance_review/${review.review_id}`)
                                                                    }
                                                                >
                                                                    {needsRemark ? 'Add remark' : 'View'}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                </Row>
            </div>

            <Dialog
                header={
                    pendingFill > 0
                        ? 'Performance review pending'
                        : 'Manager remark requested'
                }
                visible={
                    showPendingPopup &&
                    (pendingFill > 0 || pendingRemarks.length > 0) &&
                    !loading
                }
                onHide={closePendingPopup}
                style={{ width: '420px' }}
                appendTo={typeof document !== 'undefined' ? document.body : null}
                baseZIndex={12000}
                modal
                dismissableMask
                footer={
                    <div className="d-flex justify-content-end gap-2">
                        <PrimeButton
                            label="Later"
                            severity="secondary"
                            className="py-2 border-0"
                            onClick={closePendingPopup}
                        />
                        {firstPending ? (
                            <PrimeButton
                                label="Fill review"
                                icon="pi pi-arrow-right"
                                className="py-2 border-0"
                                onClick={() => {
                                    closePendingPopup();
                                    navigate(`/Graphe/dashboard/my_performance_review/${firstPending.review_id}`);
                                }}
                                autoFocus
                            />
                        ) : firstPendingRemark ? (
                            <PrimeButton
                                label="Add remark"
                                icon="pi pi-arrow-right"
                                className="py-2 border-0"
                                onClick={() => {
                                    closePendingPopup();
                                    navigate(`/Graphe/dashboard/my_performance_review/${firstPendingRemark.review_id}`);
                                }}
                                autoFocus
                            />
                        ) : null}
                    </div>
                }
            >
                {pendingFill > 0 ? (
                    <>
                        <p className="mb-2">
                            You have <strong>{pendingFill}</strong> performance self-review
                            {pendingFill === 1 ? '' : 's'} to complete.
                        </p>
                        {firstPending && (
                            <p className="text-muted small mb-0">
                                {firstPending.template_name || 'Self-review'}
                                {' · '}
                                {moment(firstPending.review_period_start).format('DD MMM YYYY')}
                                {' – '}
                                {moment(firstPending.review_period_end).format('DD MMM YYYY')}
                                {pendingFill > 1 ? ` (+${pendingFill - 1} more)` : ''}
                            </p>
                        )}
                        {pendingRemarks.length > 0 && (
                            <p className="text-muted small mt-2 mb-0">
                                You also have <strong>{pendingRemarks.length}</strong> report
                                review{pendingRemarks.length === 1 ? '' : 's'} awaiting your remark.
                            </p>
                        )}
                    </>
                ) : (
                    <p className="mb-0">
                        You have <strong>{pendingRemarks.length}</strong> report self-review
                        {pendingRemarks.length === 1 ? '' : 's'} awaiting your manager remark.
                    </p>
                )}
            </Dialog>
        </div>
    );
};

export default MyPerformanceReviews;
